import { Component } from '@theme/component';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/** Hoe ver de stapel (in breedtes van de illustratie) onder de bovenkant van de stap begint. */
const STACK_TOP_RATIO = 0.27;
/** Minimale bovenmarge, en de waarde tot er gemeten is (staat ook in de CSS). */
const MIN_OFFSET = 24;

/**
 * De stapel dozen met het plantje in "Zo werkt Triova".
 *
 * Doet twee dingen die CSS niet kan:
 * - Zet de illustratie op dezelfde hoogte als een stap rechts
 *   (`data-align-step`, standaard 3): de bovenkant van de stapel valt dan iets
 *   onder de bovenkant van die stap, wat de hoogte van de teksten ook is.
 * - Start de animatie pas als de illustratie voor 35% in beeld is, door
 *   `data-in` te zetten. Bij `prefers-reduced-motion` staat hij er meteen,
 *   zonder beweging (de animaties hangen in de CSS aan no-preference).
 */
export class HowIllustrationComponent extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    if (matchMedia(REDUCED_MOTION_QUERY).matches) {
      this.dataset.in = '';
    } else {
      this.#observer = new IntersectionObserver(this.#onIntersect, { threshold: 0.35 });
      this.#observer.observe(this);
    }

    const root = this.#root;
    if (root) {
      this.#resizeObserver = new ResizeObserver(() => this.#measure());
      this.#resizeObserver.observe(root);
    }
    document.fonts?.ready.then(() => this.#measure());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#measure();
  }

  get #root() {
    return this.closest('.how-it-works');
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onIntersect = (entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    this.dataset.in = '';
    this.#observer?.disconnect();
  };

  /**
   * Meet met offsetTop en niet met getBoundingClientRect: de stappen schuiven
   * bij het inscrollen in met `translate` (data-reveal), en dat telt daar niet mee.
   */
  #measure() {
    const root = this.#root;
    if (!(root instanceof HTMLElement) || this.offsetParent === null) return;

    const steps = root.querySelectorAll('.how-step');
    const index = Math.max(1, Number(this.dataset.alignStep) || 3);
    const step = steps[Math.min(index, steps.length) - 1];
    if (!(step instanceof HTMLElement)) return;

    const current = parseFloat(getComputedStyle(this).marginTop) || 0;
    const natural = this.#topIn(this, root) - current;
    const illustration = this.firstElementChild;
    const width = illustration instanceof HTMLElement ? illustration.offsetWidth : 0;
    const offset = Math.max(MIN_OFFSET, Math.round(this.#topIn(step, root) + STACK_TOP_RATIO * width - natural));

    this.style.setProperty('--how-illustration-offset', `${offset}px`);
  }

  /**
   * @param {HTMLElement} element
   * @param {HTMLElement} ancestor
   */
  #topIn(element, ancestor) {
    let top = 0;
    /** @type {Element | null} */
    let node = element;
    while (node instanceof HTMLElement && node !== ancestor) {
      top += node.offsetTop;
      node = node.offsetParent;
    }
    return top;
  }
}

if (!customElements.get('how-illustration-component')) {
  customElements.define('how-illustration-component', HowIllustrationComponent);
}
